/**
 * Vice District motion layer (theme setting "Animazioni allo scroll").
 *
 * - Reveal: product cards and marked section parts rise in once as they enter the viewport,
 *   staggered when several arrive together. Only elements still below the fold are hidden, and
 *   only after this script runs, so content stays visible without JS, with reduced motion and
 *   in the theme editor.
 * - Count-up: `[data-vd-count]` integers count up from zero the first time they are seen.
 */
const TARGETS = [
  '[data-vd-reveal]',
  '.section-resource-list__header',
  '.resource-list--grid > .resource-list__item',
  '.product-grid__item',
  '.vd-service__item',
  '.vd-lista__intro',
  '.vd-faq__head',
  '.vd-faq__item',
].join(', ');

const STAGGER_MS = 70;
const MAX_STAGGER_STEPS = 6;
const COUNT_MS = 1100;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const designMode = Boolean(window.Shopify?.designMode);

if (!reducedMotion && !designMode && 'IntersectionObserver' in window) {
  document.documentElement.classList.add('vd-motion');
  initReveal();
  initCountUp();
}

function initReveal() {
  const observer = new IntersectionObserver(
    (entries) => {
      const arriving = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

      arriving.forEach((entry, index) => {
        const element = /** @type {HTMLElement} */ (entry.target);
        element.style.setProperty('--vd-reveal-delay', `${Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
        element.dataset.vdRevealState = 'in';
        observer.unobserve(element);
      });
    },
    { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
  );

  const fold = window.innerHeight * 0.94;
  for (const element of document.querySelectorAll(TARGETS)) {
    if (!(element instanceof HTMLElement) || element.dataset.vdRevealState) continue;
    const rect = element.getBoundingClientRect();
    // Already on screen at load, or parked sideways in a swipe row: leave it alone
    if (rect.top < fold || rect.left >= window.innerWidth || rect.right <= 0) continue;
    element.dataset.vdRevealState = 'pending';
    observer.observe(element);
  }
}

function initCountUp() {
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.unobserve(entry.target);
        countUp(/** @type {HTMLElement} */ (entry.target));
      }
    },
    { threshold: 0.6 }
  );

  for (const element of document.querySelectorAll('[data-vd-count]')) {
    const target = Number.parseInt(element.textContent?.trim() ?? '', 10);
    if (Number.isNaN(target) || target <= 0) continue;
    if (element instanceof HTMLElement) element.dataset.vdCountTarget = String(target);
    observer.observe(element);
  }
}

/** @param {HTMLElement} element */
function countUp(element) {
  const target = Number(element.dataset.vdCountTarget);
  const final = element.textContent;
  const start = performance.now();
  // Reserve the final width so the unit next to the number does not shift while counting
  element.style.minWidth = `${element.getBoundingClientRect().width}px`;
  element.style.display = 'inline-block';

  /** @param {number} now */
  const step = (now) => {
    const progress = Math.min((now - start) / COUNT_MS, 1);
    const eased = progress === 1 ? 1 : 1 - 2 ** (-10 * progress);
    element.textContent = String(Math.round(target * eased));
    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      element.textContent = final;
    }
  };
  requestAnimationFrame(step);
}
